
import { useEffect, useRef, useState } from 'react';

export default function BrandPositioning() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Guard against browsers that do not support IntersectionObserver
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );

    const current = sectionRef.current;
    if (current) observer.observe(current);

    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  const capabilities = [
    { icon: 'ri-robot-2-line', label: 'AI Intake & Lead Qualification' },
    { icon: 'ri-flow-chart', label: 'Workflow & CRM Automation' },
    { icon: 'ri-user-voice-line', label: 'AI Assistants (Patient / Client / Case)' },
    { icon: 'ri-bar-chart-grouped-line', label: 'Analytics & Optimization' },
    { icon: 'ri-shield-check-line', label: 'Compliance-Conscious Implementation' },
  ];

  return (
    <section id="capabilities" ref={sectionRef} className="py-28 bg-[#F7F9FC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Left Column — Visual */}
          <div
            className={`transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <div className="w-full h-[480px]">
                <img
                  src="https://public.readdy.ai/ai/img_res/14b12fbd3f96a3a72ea7339c01171c4c.jpg"
                  alt="Enterprise AI dashboard interface"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E1A2B]/30 to-transparent"></div>
              {/* Floating stat badge */}
              <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm rounded-lg px-5 py-3 shadow-lg">
                <p className="text-xs text-gray-500 font-medium">Intake Efficiency</p>
                <p className="text-2xl font-semibold text-[#0E1A2B]">94.7%</p>
              </div>
            </div>
          </div>

          {/* Right Column — Content */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <h2
              className="text-3xl md:text-4xl font-semibold text-[#0E1A2B] mb-6"
              style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}
            >
              Built for Regulated & Operationally Complex Industries
            </h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed mb-10">
              <p>
                Aivina deploys enterprise‑grade AI infrastructure designed to enhance intake,
                qualification, workflow management, and reporting — without disrupting compliance
                or operational trust.
              </p>
              <p className="font-medium text-[#0E1A2B]">
                We specialize in industries where precision matters and mistakes are expensive.
              </p>
            </div>

            <div className="space-y-4">
              {capabilities.map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center bg-white rounded-xl px-5 py-4 shadow-sm border border-gray-100 hover:shadow-md hover:border-[#2FB8AC]/20 transition-all duration-300 cursor-default ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: `${300 + index * 100}ms` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#2FB8AC]/10 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-lg text-[#2FB8AC]`}></i>
                  </div>
                  <span className="ml-4 text-sm text-gray-800 font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
